import { useContext, useEffect } from 'react';
import { useRouter } from 'next/router';
import { AuthContext } from '@/contexts/AuthContext';

const publicRoutes = [
  '/', 
  '/login', 
  '/signup', 
  '/forgot-password', 
  '/magic-link-login',
  '/terms',
  '/privacy'
];

const ProtectedRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user, initializing } = useContext(AuthContext);
  const router = useRouter();
  
  // Immediately render public routes and event pages without waiting for auth
  const isEventPage = router.pathname.startsWith('/events/');
  const isPublicRoute = publicRoutes.includes(router.pathname);
  
  if (isPublicRoute || isEventPage) {
    return <>{children}</>;
  }

  useEffect(() => {
    if (!initializing && !user) {
      // Encode the current path to use as a redirect parameter
      const currentPath = encodeURIComponent(router.asPath);
      router.push(`/login?redirect=${currentPath}`);
    }
  }, [user, initializing, router]);

  // For protected routes, show loading only if we're still initializing
  if (initializing) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  // Don't render protected content if user is not authenticated
  if (!user) {
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;